/**
 * L1 结构化 API 错误
 * 供 request 拦截器与 presenter 共用
 */

/** 网络层失败码（超时、无响应体等） */
export const NETWORK_ERROR = 0

/** 无 L1 信封时的兜底文案（presenter 可用 i18n 覆盖） */
const DEFAULT_NETWORK_MESSAGE = '网络请求失败'

/** L1 响应信封（错误体） */
interface L1ErrorEnvelope {
  code: number
  message: string
  data?: unknown
}

/** axios 风格错误（含 response） */
interface AxiosLikeError {
  response?: {
    status?: number
    data?: unknown
  }
  message?: string
}

/**
 * L1 API 结构化错误
 */
export class L1Error extends Error {
  readonly code: number
  readonly httpStatus: number
  readonly data?: unknown

  constructor(
    code: number,
    message: string,
    httpStatus: number,
    data?: unknown
  ) {
    super(message)
    this.name = 'L1Error'
    this.code = code
    this.httpStatus = httpStatus
    this.data = data
    Object.setPrototypeOf(this, L1Error.prototype)
  }
}

/**
 * 判断值是否为 L1Error 实例
 */
export function isL1Error(value: unknown): value is L1Error {
  return value instanceof L1Error
}

/**
 * 判断 body 是否为 L1 错误信封
 */
function isL1ErrorEnvelope(data: unknown): data is L1ErrorEnvelope {
  return (
    data !== null &&
    typeof data === 'object' &&
    'code' in data &&
    typeof (data as L1ErrorEnvelope).code === 'number' &&
    'message' in data &&
    typeof (data as L1ErrorEnvelope).message === 'string'
  )
}

/**
 * 从响应体解析 L1 错误信封
 */
export function parseL1Error(data: unknown, httpStatus: number): L1Error {
  if (isL1ErrorEnvelope(data)) {
    return new L1Error(data.code, data.message, httpStatus, data.data)
  }
  return new L1Error(NETWORK_ERROR, DEFAULT_NETWORK_MESSAGE, httpStatus)
}

/**
 * 将任意输入规范化为 L1Error
 */
export function toL1Error(input: unknown, httpStatus = 0): L1Error {
  if (isL1Error(input)) {
    return input
  }

  if (input !== null && typeof input === 'object') {
    const axiosLike = input as AxiosLikeError
    if (axiosLike.response !== undefined) {
      const status = axiosLike.response.status ?? httpStatus
      if (axiosLike.response.data !== undefined) {
        return parseL1Error(axiosLike.response.data, status)
      }
      return new L1Error(
        NETWORK_ERROR,
        axiosLike.message || DEFAULT_NETWORK_MESSAGE,
        status
      )
    }
    if (typeof axiosLike.message === 'string' && axiosLike.message.length > 0) {
      return new L1Error(NETWORK_ERROR, axiosLike.message, httpStatus)
    }
  }

  if (input instanceof Error) {
    return new L1Error(NETWORK_ERROR, input.message, httpStatus)
  }

  return new L1Error(NETWORK_ERROR, DEFAULT_NETWORK_MESSAGE, httpStatus)
}
